import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationItem } from './NotificationItem';
import { dashboardRoutePaths } from '../../routes/route-config';

interface NotificationListProps {
  onClose: () => void;
}

export const NotificationList = ({ onClose }: NotificationListProps) => {
  const { t } = useTranslation();
  const { notifications, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleViewAll = () => {
    onClose();
    navigate(dashboardRoutePaths.notifications);
  };

  return (
    <div className="flex flex-col">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <h3 className="text-base font-semibold text-neutral-800">
          {t('notifications.notificationList.notifications')}
        </h3>
        {notifications.length > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs font-medium text-info-600 hover:text-info-800"
          >
            {t('notifications.notificationList.markAllAsRead')}
          </button>
        )}
      </div>

      {/* list — capped at 5 items in the dropdown */}
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-500">
            {t('notifications.notificationList.noNotifications')}
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {notifications.slice(0, 5).map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={onClose}
              />
            ))}
          </div>
        )}
      </div>

      {/* footer — always visible */}
      <div className="border-t border-neutral-200">
        <button
          onClick={handleViewAll}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium
            text-info-600 hover:text-info-800 hover:bg-neutral-50 transition-colors"
        >
          {t('notifications.notificationList.viewAllNotifications')}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
